/**
 * MomentButton — Floating emergency regulation button.
 * One tap → user's own voice guides a 60-second breath/grounding sequence.
 * Lives in App.js above all routes. Only shows when user has a cloned voice.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Analytics from '@/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 60-second scripts — rotated randomly each time
const SCRIPTS = [
  `You are here. Right now, this moment is all there is. Breathe in slowly — four counts. Hold. Now release, six counts. Feel your feet on the ground. Your body knows how to do this. Breathe in again. Let your shoulders drop. You are safe. Whatever is happening, this breath is yours. In. Hold. Out. The wave will pass. You have everything you need right now. One more breath. In. And out. You are okay.`,

  `Notice where you are. Feel the surface beneath you. Breathe in through your nose — let your belly expand. Hold it gently. Now breathe out, slowly, fully. Again. In. Hold. Out. Name three things you can see right now. Your nervous system is listening to you. Breathe in. Let your jaw unclench. Your hands relax. Out. You are not the storm. You are the one watching it. In. Out. Present. Safe. Here.`,

  `This is your voice. It knows you. Breathe with it now. In — slow, deep. Hold at the top. Out — long and steady. Good. Again. Your heart rate is slowing. This is physiological. This is real. In. Feel your back against whatever holds you. Out. You are bigger than this moment. In. The body leads — the mind follows. Out. One more. In. And out. You did it. You are here.`,
];

const PHASES = [
  { label: 'Breathe in',  duration: 4000 },
  { label: 'Hold',        duration: 2000 },
  { label: 'Breathe out', duration: 6000 },
];

export default function MomentButton() {
  const [user, setUser]         = useState(null);
  const [hasVoice, setHasVoice] = useState(false);
  const [open, setOpen]         = useState(false);
  const [phase, setPhase]       = useState('idle'); // idle | loading | playing | done
  const [breathPhase, setBreathPhase] = useState(0);
  const [elapsed, setElapsed]   = useState(0);
  const audioRef  = useRef(null);
  const timerRef  = useRef(null);
  const breathRef = useRef(null);

  useEffect(() => {
    const raw = localStorage.getItem('user');
    if (!raw) return;
    try {
      const u = JSON.parse(raw);
      setUser(u);
      if (u.cloned_voice_status === 'ready') setHasVoice(true);
    } catch {}
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    clearInterval(timerRef.current);
    clearInterval(breathRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  if (!hasVoice) return null;

  const startMoment = async () => {
    Analytics.momentStarted();
    setPhase('loading');
    setElapsed(0);
    setBreathPhase(0);

    const script = SCRIPTS[Math.floor(Math.random() * SCRIPTS.length)];

    try {
      const res = await axios.post(`${API}/voice/tts`, {
        user_id: user.user_id,
        text: script,
        voice_mode: 'self',
        stability: 0.75,
        similarity_boost: 0.88,
        style: 0.15,
        use_speaker_boost: true,
      });

      const audio = new Audio(res.data.audio_url);
      audioRef.current = audio;

      audio.onended = () => {
        clearInterval(timerRef.current);
        clearInterval(breathRef.current);
        Analytics.momentCompleted();
        setPhase('done');
      };

      audio.play();
      setPhase('playing');

      // Elapsed counter (60s)
      timerRef.current = setInterval(() => {
        setElapsed(e => {
          if (e >= 60) { clearInterval(timerRef.current); return 60; }
          return e + 1;
        });
      }, 1000);

      // Breath phase rotation
      let bIdx = 0, bElapsed = 0;
      breathRef.current = setInterval(() => {
        bElapsed += 200;
        if (bElapsed >= PHASES[bIdx].duration) {
          bElapsed = 0;
          bIdx = (bIdx + 1) % PHASES.length;
          setBreathPhase(bIdx);
        }
      }, 200);

    } catch {
      setPhase('idle');
    }
  };

  const stop = () => {
    if (phase === 'playing') Analytics.momentAbandoned();
    clearInterval(timerRef.current);
    clearInterval(breathRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPhase('idle');
    setOpen(false);
  };

  const dismiss = () => {
    stop();
    setOpen(false);
    setPhase('idle');
  };

  const BREATH_LABELS = PHASES.map(p => p.label);
  const progressPct = Math.round((elapsed / 60) * 100);

  return (
    <>
      {/* ── Floating trigger ── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Moment of Need"
          style={{
            position: 'fixed',
            bottom: '88px',
            right: '18px',
            zIndex: 9000,
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
            border: 'none',
            boxShadow: '0 4px 20px rgba(30,107,104,0.4)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'momentPulse 4s ease-in-out infinite',
          }}
        >
          {/* Breath wave icon */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M2 12 C5 8, 8 16, 12 12 C16 8, 19 16, 22 12"
              stroke="#fff" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
          </svg>
        </button>
      )}

      <style>{`
        @keyframes momentPulse {
          0%,100% { box-shadow: 0 4px 20px rgba(30,107,104,0.4), 0 0 0 0 rgba(30,107,104,0.3); }
          50%      { box-shadow: 0 4px 20px rgba(30,107,104,0.4), 0 0 0 10px rgba(30,107,104,0); }
        }
        @keyframes rippleOut {
          0%   { transform: translate(-50%,-50%) scale(0.05); opacity: 0.8; }
          100% { transform: translate(-50%,-50%) scale(1);    opacity: 0; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Full-screen overlay ── */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9500,
          background: 'linear-gradient(160deg, #071A19 0%, #0D2B2A 50%, #1E4A47 100%)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          animation: 'fadeInUp 0.4s ease',
        }}>

          {/* Close */}
          <button
            onClick={dismiss}
            style={{
              position: 'absolute', top: '20px', right: '20px',
              background: 'rgba(255,255,255,0.08)', border: 'none',
              color: 'rgba(255,255,255,0.5)', borderRadius: '8px',
              padding: '8px 14px', fontSize: '0.8rem', cursor: 'pointer',
            }}
          >
            ✕
          </button>

          {phase === 'idle' && (
            <div style={{ textAlign: 'center', padding: '0 32px' }}>
              {/* Ripple orb */}
              <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto 36px' }}>
                {[0, 0.8, 1.6, 2.4].map((d, i) => (
                  <div key={i} style={{
                    position: 'absolute', top: '50%', left: '50%',
                    width: '110px', height: '110px', borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.4)',
                    animation: `rippleOut 3.2s ease-out ${d}s infinite`,
                  }} />
                ))}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  width: '14px', height: '14px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 0 20px rgba(255,255,255,0.4)',
                }} />
              </div>

              <p style={{ margin: '0 0 8px', fontSize: '0.65rem', fontWeight: '700', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Moment of Need
              </p>
              <h2 style={{ margin: '0 0 14px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.6rem', fontWeight: '400' }}>
                Your voice.<br />Right now.
              </h2>
              <p style={{ margin: '0 0 36px', color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                60 seconds. Your own voice guides you back.
              </p>
              <button
                onClick={startMoment}
                style={{
                  padding: '18px 48px',
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '50px', color: '#fff',
                  fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
                  letterSpacing: '0.04em',
                }}
              >
                Begin →
              </button>
            </div>
          )}

          {phase === 'loading' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 24px' }}>
                {[0, 0.6, 1.2].map((d, i) => (
                  <div key={i} style={{
                    position: 'absolute', top: '50%', left: '50%',
                    width: '70px', height: '70px', borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.4)',
                    animation: `rippleOut 1.8s ease-out ${d}s infinite`,
                  }} />
                ))}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.9)',
                }} />
              </div>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem' }}>Calling your voice…</p>
            </div>
          )}

          {phase === 'playing' && (
            <div style={{ textAlign: 'center', padding: '0 32px', width: '100%', maxWidth: '360px' }}>
              {/* Breath indicator */}
              <div style={{ position: 'relative', width: '140px', height: '140px', margin: '0 auto 28px' }}>
                {/* Outer ring animates with breath */}
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  border: '1.5px solid rgba(255,255,255,0.2)',
                  transform: breathPhase === 0 ? 'scale(1.15)' : breathPhase === 1 ? 'scale(1.15)' : 'scale(0.85)',
                  transition: `transform ${PHASES[breathPhase].duration}ms ease-in-out`,
                }} />
                <div style={{
                  position: 'absolute', inset: '20px', borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.12)',
                  transform: breathPhase === 0 ? 'scale(1.1)' : breathPhase === 2 ? 'scale(0.9)' : 'scale(1.1)',
                  transition: `transform ${PHASES[breathPhase].duration}ms ease-in-out`,
                }} />
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  width: '12px', height: '12px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.85)',
                  boxShadow: '0 0 16px rgba(255,255,255,0.4)',
                }} />
              </div>

              <p style={{ margin: '0 0 6px', fontSize: '1.1rem', color: 'rgba(255,255,255,0.9)', fontWeight: '300', letterSpacing: '0.06em', minHeight: '1.6em' }}>
                {BREATH_LABELS[breathPhase]}
              </p>

              {/* Progress bar */}
              <div style={{ margin: '24px 0 16px', height: '2px', background: 'rgba(255,255,255,0.1)', borderRadius: '1px' }}>
                <div style={{
                  height: '100%',
                  width: `${progressPct}%`,
                  background: 'rgba(255,255,255,0.5)',
                  borderRadius: '1px',
                  transition: 'width 1s linear',
                }} />
              </div>
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>
                {60 - elapsed}s remaining
              </p>

              <button
                onClick={stop}
                style={{
                  marginTop: '28px', background: 'none', border: 'none',
                  color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem',
                  cursor: 'pointer', padding: '8px',
                }}
              >
                Stop
              </button>
            </div>
          )}

          {phase === 'done' && (
            <div style={{ textAlign: 'center', padding: '0 32px' }}>
              <div style={{ position: 'relative', width: '90px', height: '90px', margin: '0 auto 28px' }}>
                {[0, 0.9, 1.8].map((d, i) => (
                  <div key={i} style={{
                    position: 'absolute', top: '50%', left: '50%',
                    width: '80px', height: '80px', borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.35)',
                    animation: `rippleOut 2.8s ease-out ${d}s infinite`,
                  }} />
                ))}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                  color: 'rgba(255,255,255,0.85)', fontSize: '1.2rem',
                }}>✓</div>
              </div>
              <h2 style={{ margin: '0 0 12px', fontFamily: "'Playfair Display', serif", color: '#fff', fontSize: '1.4rem', fontWeight: '400' }}>
                You're back.
              </h2>
              <p style={{ margin: '0 0 32px', color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                60 seconds. That's all it took.
              </p>
              <button
                onClick={dismiss}
                style={{
                  padding: '14px 36px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '50px', color: '#fff',
                  fontSize: '0.9rem', cursor: 'pointer',
                }}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
